<template>
    <div>
        <modal
            id="work-orders-modal"
            size="lg"
            ok-title="Save"
            scrollable
            :title="title"
            :visible="show"
            :okDisabled="invalid || !canEdit || invalidDateRange || globalInvalid"
            @ok="onWorkOrderSave"
            @change="onWorkOrderVisibilityChange"
            class="lx-global-modal"
        >
            <busy-modal :show="busy || modalBusy"></busy-modal>
            <b-container fluid>
                <b-row v-if="hasPublishedOwnerStatement">
                    <b-col>
                        <b-alert variant="warning" show>
                            Warning! This work order has already been reconciled to a published owner statement. Any
                            changes will not be reflected on current owner statements.
                        </b-alert>
                    </b-col>
                </b-row>
                <validation-observer v-slot="{ meta }" ref="observer" :initial-values="workOrderToSave">
                    {{ setInvalid(!meta.valid) }}
                    <b-row class="my-2">
                        <b-col md="2">
                            <label for="work-orders-number">Work Order #:</label>
                        </b-col>
                        <b-col>
                            <form-checkbox
                                v-if="showAutogenerateWorkOrderNumber"
                                v-model="autogenerateWorkOrderNumber"
                                class="mb-2"
                            >
                                Autogenerate work order number
                            </form-checkbox>
                            <validation-provider
                                v-if="!autogenerateWorkOrderNumber || !showAutogenerateWorkOrderNumber"
                                name="number"
                                v-slot="{ errors, handleChange }"
                                rules="required|validInteger"
                            >
                                <form-input
                                    trim
                                    class="mb-2"
                                    id="work-orders-number"
                                    v-model="workOrderToSave.number"
                                    :disabled="!canEdit"
                                    :error="errors[0]"
                                    @blur="checkWorkOrderNumber"
                                    @keypress="maskInputNumber($event)"
                                    @input="handleChange"
                                ></form-input>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row class="my-2">
                        <b-col md="2">
                            <label for="work-orders-status">Status:</label>
                        </b-col>
                        <b-col>
                            <validation-provider name="status" v-slot="{ errors, handleChange }">
                                <form-select
                                    class="mb-2"
                                    id="work-orders-status"
                                    v-model="workOrderToSave.status"
                                    :disabled="!canEdit"
                                    :options="workOrderStatuses"
                                    :error="errors[0]"
                                    @change="handleChange"
                                ></form-select>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row class="my-2">
                        <b-col md="2">
                            <label for="work-orders-property">Property:</label>
                        </b-col>
                        <b-col>
                            <validation-provider name="rentalProperty.id" v-slot="{ errors }">
                                <form-select
                                    class="mb-2"
                                    id="work-orders-property"
                                    v-model="workOrderToSave.rentalProperty.id"
                                    @change="onPropertyChange"
                                    :disabled="!canEdit"
                                    :options="workOrderProperties"
                                    :error="errors[0]"
                                ></form-select>
                            </validation-provider>
                            <validation-provider name="rentalProperty.other" v-slot="{ errors }">
                                <form-input
                                    trim
                                    v-if="workOrderToSave.rentalProperty.id === 0"
                                    id="work-orders-property-other"
                                    class="md-2"
                                    v-model="workOrderToSave.rentalProperty.other"
                                    :disabled="!canEdit"
                                    :error="errors[0]"
                                ></form-input>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row class="work-orders__reporter my-2">
                        <b-col md="2">
                            <label for="work-orders-reporter">Reported By:</label>
                        </b-col>
                        <b-col>
                            <b-button-group class="work-orders__reporter-group">
                                <b-col md="4" class="work-orders__reporter-select">
                                    <validation-provider name="reporter.type" v-slot="{ errors }">
                                        <form-select
                                            id="work-orders-reporter"
                                            v-model="workOrderToSave.reporter.type"
                                            @change="onReporterTypeChange"
                                            :options="filteredWorkOrderReporterTypes"
                                            :disabled="!canEdit || !hasModifyAccess"
                                            :error="errors[0]"
                                        ></form-select>
                                    </validation-provider>
                                </b-col>
                                <b-col class="work-orders__reporter-option">
                                    <validation-provider name="reporter.value" v-slot="{ errors }">
                                        <form-input
                                            trim
                                            v-if="workOrderToSave.reporter.type === 'OTHER'"
                                            id="work-orders-reporter-other"
                                            class="md-2"
                                            v-model="workOrderToSave.reporter.value"
                                            :disabled="!canEdit || !hasModifyAccess"
                                            :error="errors[0]"
                                        ></form-input>
                                    </validation-provider>
                                    <validation-provider name="reporter.value" v-slot="{ errors }">
                                        <form-select
                                            v-if="
                                                workOrderToSave.reporter.type === 'TEAM_MEMBER' ||
                                                workOrderToSave.reporter.type === 'OWNER'
                                            "
                                            class="md-2"
                                            id="work-orders-reporter-option"
                                            v-model="workOrderToSave.reporter.value"
                                            :disabled="!canEdit || !hasModifyAccess"
                                            :options="workOrderReporters"
                                            :error="errors[0]"
                                        ></form-select>
                                    </validation-provider>
                                </b-col>
                            </b-button-group>
                        </b-col>
                    </b-row>
                    <b-row class="my-2">
                        <b-col md="2">
                            <label for="work-orders-who-pay">Who Will Pay:</label>
                        </b-col>
                        <b-col>
                            <validation-provider name="accountAssignee" v-slot="{ errors }">
                                <form-select
                                    class="mb-2"
                                    id="work-orders-who-pay"
                                    v-model="workOrderToSave.accountAssignee"
                                    :disabled="!canEdit"
                                    :options="workOrderWhoWillPay"
                                    :error="errors[0]"
                                ></form-select>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row class="my-2">
                        <b-col md="2">
                            <label for="work-orders-account">Account:</label>
                        </b-col>
                        <b-col>
                            <b-button-group class="work-orders__account">
                                <validation-provider
                                    name="account"
                                    v-slot="{ errors }"
                                    :style="{ width: '95%' }"
                                    as="div"
                                >
                                    <form-select
                                        id="work-orders-account"
                                        v-model="workOrderToSave.account"
                                        :disabled="!canEdit"
                                        :options="workOrderAccountExpenses"
                                        :error="errors[0]"
                                    ></form-select>
                                </validation-provider>
                                <responsive-button
                                    :disabled="!canEdit"
                                    :style="{ marginRight: '0px !important' }"
                                    :icon="['fas', 'plus']"
                                    class="work-orders__account-add"
                                    variant="primary"
                                    @click="openAddNewExpenseAccountModal"
                                ></responsive-button>
                            </b-button-group>
                        </b-col>
                    </b-row>
                    <b-row class="my-2" v-if="!!workOrderToSave.damage_notice_source">
                        <b-col md="2">
                            <span class="label">Source:</span>
                        </b-col>
                        <b-col>
                            <div class="form-group">
                                <a
                                    :href="
                                        `/app/guest/${workOrderToSave.damage_notice_source.guest_id}/invoices/` +
                                        `?reservation=${workOrderToSave.damage_notice_source.meta_reservation_id}` +
                                        '&tab=housekeeping'
                                    "
                                    target="_blank"
                                >
                                    Damage Notice - Invoice #{{
                                        workOrderToSave.damage_notice_source.meta_reservation_id
                                    }}
                                </a>
                            </div>
                        </b-col>
                    </b-row>
                    <b-row>
                        <work-orders-info
                            :actionType="actionType"
                            :canEdit="canEdit"
                            @busy="setBusy"
                        ></work-orders-info>
                    </b-row>
                </validation-observer>
            </b-container>
        </modal>
        <expense-account-modal
            :showModal="isExpenseAccountModalShown"
            :expenseAccount="expenseAccount"
            @save="onExpenseAccountSave"
            @modalHide="onExpenseAccountModalHide"
        ></expense-account-modal>
    </div>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex';
    import API from '../../../api';
    import { maskNumber } from '../../../utils/objects';
    import Modal from '../../generic/modal/modal.vue';
    import LargeModal from '../../generic/large-modal/large-modal.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import WorkOrdersInfo from './work-orders-info/work-orders-info.vue';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import ExpenseAccountModal from '../work-orders-configuration-options/expense-account-modal.vue';
    import format from 'date-fns/format';
    import FormCheckbox from '../../generic/form-checkbox/form-checkbox.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import FormInputGroup from '../../generic/form-input-group/form-input-group.vue';
    import { WorkOrderActionType, ActorType } from '../../../constants';
    import { getNextAccountExpenseNumber } from '../../../utils/account-expense';

    const DEFAULT_STATUS = 'Open';
    const DEFAULT_JOB_TYPE = 'Cleaning';
    const DEFAULT_PRIORITY = 'High';
    const MODAL_TIMEOUT = 200;

    export default {
        name: 'WorkOrdersModal',
        components: {
            Modal,
            LargeModal,
            BusyModal,
            ResponsiveButton,
            WorkOrdersInfo,
            ExpenseAccountModal,
            FormCheckbox,
            FormSelect,
            FormTextarea,
            FormInput,
            FormInputGroup,
        },
        provide() {
            return {
                setGlobalInvalid: (value) => {
                    this.globalInvalid = value;
                },
            };
        },
        props: {
            actionType: {
                type: String,
                default: WorkOrderActionType.Create,
            },
            workOrderToEditNumber: {
                type: String,
                default: '',
            },
            show: {
                type: Boolean,
                default: false,
            },
            canEdit: {
                type: Boolean,
                default: true,
            },
            hasModifyAccess: {
                type: Boolean,
                default: true,
            },
            modalBusy: {
                type: Boolean,
                default: false,
            },
            hasPublishedOwnerStatement: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                busy: false,
                invalid: false,
                globalInvalid: false,
                autogenerateWorkOrderNumber: true,
                showAutogenerateWorkOrderNumber: true,
                // flag to skip double loader on dialog open
                isModalShown: false,
                invalidDateRange: false,
                isExpenseAccountModalShown: false,
                workOrderNumber: null,
                expenseAccount: {},
            };
        },
        computed: {
            ...mapGetters('header', ['userID', 'userIsTeamMember', 'userIsOwner']),
            ...mapGetters('workOrders', [
                'accountExpenses',
                'priorities',
                'jobTypes',
                'statuses',
                'teamMembers',
                'vendors',
                'formProperties',
                'propertyOwners',
                'workOrderToSave',
                'metadata',
            ]),
            title() {
                return this.actionType === WorkOrderActionType.Edit ? 'Edit Work Order' : 'Add Work Order';
            },
            workOrderStatuses() {
                const isCloseDisabled = this.metadata.permissions && this.metadata.permissions.is_close_disabled;
                return [
                    ...this.statuses.map((status) => ({
                        value: status.id,
                        text: status.name,
                        disabled: isCloseDisabled && status.name === 'Closed',
                    })),
                ];
            },
            workOrderProperties() {
                const other = { value: 0, text: 'Other' };
                const properties = [
                    ...this.formProperties.map((property, idx) => ({
                        value: property.r_or_p_id,
                        text: property.name,
                    })),
                ];
                properties.push(other);
                return properties;
            },
            filteredWorkOrderReporterTypes() {
                return this.workOrderReporterTypes.filter((optionType) => optionType.enabled);
            },
            workOrderAccountExpenses() {
                return [
                    ...this.accountExpenses.map((expense, idx) => ({
                        value: expense.id,
                        text: `${expense.number} - ${expense.name}`,
                    })),
                ];
            },
            workOrderReporterTypes() {
                return [
                    {
                        value: ActorType.Subscriber,
                        text: 'Subscriber',
                        enabled: true,
                    },
                    {
                        value: ActorType.TeamMember,
                        text: 'Team Member',
                        enabled: true,
                    },
                    { value: ActorType.Owner, text: 'Owner', enabled: this.propertyOwners.length > 0 },
                    { value: ActorType.Other, text: 'Other', enabled: true },
                ];
            },
            workOrderWhoWillPay() {
                if (this.propertyOwners.length > 0) {
                    return [
                        ...this.propertyOwners.map(this.mapSelectOption),
                        { value: ActorType.Subscriber, text: 'Subscriber' },
                    ];
                } else {
                    return [{ value: ActorType.Subscriber, text: 'Subscriber' }];
                }
            },
            workOrderReporters() {
                if (this.workOrderToSave.reporter.type === ActorType.Owner) {
                    return [...this.propertyOwners.map(this.mapSelectOption)];
                } else if (this.workOrderToSave.reporter.type === ActorType.TeamMember) {
                    return [...this.teamMembers.map(this.mapSelectOption)];
                } else {
                    return [];
                }
            },
            errors() {
                return [];
            },
        },
        methods: {
            ...mapActions('workOrders', ['fetchAccountExpenses', 'fetchPropertyOwners', 'updateWorkOrderToSave']),
            setInvalid(isInvalid) {
                this.invalid = isInvalid;
            },
            onWorkOrderSave(event) {
                event.preventDefault();
                this.$emit('workOrderSave', this.workOrderToSave);
            },
            onWorkOrderVisibilityChange(isVisible) {
                if (!isVisible) {
                    this.$emit('modalClose');
                }
            },
            maskInputNumber(evt) {
                return maskNumber(evt);
            },
            openAddNewExpenseAccountModal() {
                this.expenseAccount = {
                    number: getNextAccountExpenseNumber(this.accountExpenses),
                };
                this.isExpenseAccountModalShown = true;
            },
            onExpenseAccountModalHide() {
                this.isExpenseAccountModalShown = false;
                setTimeout(() => {
                    this.expenseAccount = {};
                }, MODAL_TIMEOUT);
            },
            async setInitialWorkOrderData(data = {}) {
                const getInitialStatus = (_) => {
                    const defaultStatus = this.statuses.find((status) => status.name === DEFAULT_STATUS);
                    return defaultStatus ? defaultStatus.id : '';
                };

                const getInitialRentalProperty = (_) => {
                    return this.formProperties.length ? this.formProperties[0].r_or_p_id : 0;
                };

                const getInitialAccount = (_) => {
                    return this.accountExpenses.length ? this.accountExpenses[0].id : '';
                };

                const getInitialReporter = (_) => {
                    if (this.userIsTeamMember) {
                        const defaultTeamMember = this.teamMembers.find(
                            (team_member) => team_member.id === this.userID
                        );
                        if (defaultTeamMember) {
                            return {
                                type: ActorType.TeamMember,
                                value: defaultTeamMember.id,
                            };
                        }
                    } else if (this.userIsOwner) {
                        const defaultOwner = this.propertyOwners.find((owner) => owner.id === this.userID);
                        if (defaultOwner) {
                            return {
                                type: ActorType.Owner,
                                value: defaultOwner.id,
                            };
                        }
                    }

                    return {
                        type: ActorType.Subscriber,
                        value: '',
                    };
                };

                const getInitialAccountAssignee = (_) => {
                    if (this.userIsOwner) {
                        const defaultOwner = this.propertyOwners.find((owner) => owner.id === this.userID);
                        if (defaultOwner) {
                            return defaultOwner.id;
                        }
                    }

                    const defaultOwner = this.propertyOwners[0];
                    if (defaultOwner) {
                        return defaultOwner.id;
                    }

                    return ActorType.Subscriber;
                };

                const getInitialJobType = (_) => {
                    const defaultJobType = this.jobTypes.find((jobType) => jobType.title === DEFAULT_JOB_TYPE);
                    return defaultJobType ? defaultJobType.id : '';
                };

                const getInitialPriority = (_) => {
                    const defaultPriority = this.priorities.find((priority) => priority.name === DEFAULT_PRIORITY);
                    return defaultPriority ? defaultPriority.id : '';
                };

                const rentalProperty = data.rentalProperty || {
                    id: getInitialRentalProperty(),
                    other: '',
                };
                await this.getPropertyOwners(rentalProperty.id);

                const initialData = {
                    number: data.number || this.workOrderNumber,
                    status: data.status || getInitialStatus(),
                    rentalProperty,
                    assignee: {
                        type: '',
                        value: '',
                    },
                    notifier: {
                        type: '',
                        value: '',
                    },
                    reporter: data.reporter || getInitialReporter(),
                    accountAssignee: data.accountAssignee || getInitialAccountAssignee(),
                    account: data.account || getInitialAccount(),
                    jobType: getInitialJobType(),
                    priority: getInitialPriority(),
                    dateReported: format(new Date(), 'yyyy-MM-dd'),
                    photos: [],
                };
                this.updateWorkOrderToSave(initialData);
            },
            mapSelectOption(reporter = {}) {
                return {
                    value: reporter.id,
                    text: `${reporter.first_name || ''} ${reporter.last_name || ''} (${reporter.email || '-'})`,
                };
            },
            async onExpenseAccountSave(expenseAccount = {}) {
                await this.dispatchRequest(
                    async (_) => {
                        const createdAccountExpense = await API.post(
                            `/app/work-orders/api/account-expenses/`,
                            expenseAccount
                        );
                        await this.fetchAccountExpenses();
                        this.workOrderToSave.account = createdAccountExpense.data.id;
                    },
                    `Could not create expense account.`,
                    true
                );
            },
            async getPropertyOwners(id) {
                await this.dispatchRequest(
                    (_) =>
                        this.fetchPropertyOwners({
                            id: id,
                        }),
                    'Could not load property owners.',
                    this.isModalShown
                );
            },
            async dispatchRequest(request, errorMessage = '', withLoader = false) {
                const toggleLoader = (busy) => {
                    if (withLoader) {
                        this.busy = busy;
                    }
                };

                try {
                    toggleLoader(true);
                    await request();
                } catch {
                    this.errorToast({ text: errorMessage });
                } finally {
                    toggleLoader(false);
                }
            },
            async checkWorkOrderNumber($event) {
                const value = $event instanceof Event ? $event.target.value : $event;
                const isEditSameNumber = (_) => {
                    return (
                        this.actionType === WorkOrderActionType.Edit &&
                        Number(this.workOrderToEditNumber) === Number(value)
                    );
                };

                if (!isEditSameNumber()) {
                    try {
                        const response = await API.post(`/app/work-orders/api/orders/check-workorder/`, {
                            work_order_number: value,
                        });
                        if (response.data && response.data.status === 'ERROR') {
                            this.$refs.observer.setErrors({
                                number: [`Not unique order number.`],
                            });
                        }
                    } catch {}
                }
            },
            setBusy(busy) {
                this.busy = busy;
            },
            async onPropertyChange(id) {
                await this.getPropertyOwners(id);
                if (this.propertyOwners.length > 0) {
                    const hasAccountAssigneeId = (_) => {
                        return this.propertyOwners
                            .map((item) => item.id)
                            .includes(this.workOrderToSave.accountAssignee);
                    };
                    const hasReporterId = (_) => {
                        return this.propertyOwners.map((item) => item.id).includes(this.workOrderToSave.reporter.value);
                    };

                    if (!hasAccountAssigneeId()) {
                        this.updateWorkOrderToSave({
                            accountAssignee: this.propertyOwners[0].id,
                        });
                    }

                    if (this.workOrderToSave.reporter.type === ActorType.Owner) {
                        if (!hasReporterId()) {
                            this.workOrderToSave.reporter.type = ActorType.Subscriber;
                        }
                    }
                } else {
                    if (this.workOrderToSave.reporter.type === ActorType.Owner) {
                        this.updateWorkOrderToSave({
                            reporter: { type: ActorType.Subscriber },
                        });
                    }

                    this.updateWorkOrderToSave({
                        accountAssignee: ActorType.Subscriber,
                    });
                }
            },
            onReporterTypeChange(type) {
                if (type !== ActorType.Owner && type !== ActorType.TeamMember) {
                    this.updateWorkOrderToSave({
                        reporter: {
                            type: type,
                            value: '',
                        },
                    });
                } else if (this.workOrderReporters.length > 0) {
                    const reporter = this.workOrderReporters[0];
                    this.updateWorkOrderToSave({
                        reporter: {
                            type: type,
                            value: reporter.value,
                        },
                    });
                }
            },
        },
        watch: {
            show(isShown) {
                if (isShown) {
                    (async () => {
                        this.showAutogenerateWorkOrderNumber = this.actionType === WorkOrderActionType.Create;
                        this.autogenerateWorkOrderNumber = this.actionType === WorkOrderActionType.Create;
                        // set default initial work order data on create modal open
                        if (this.actionType === WorkOrderActionType.Create) {
                            await this.setInitialWorkOrderData();
                        }
                        setTimeout((_) => (this.isModalShown = true));
                    })();
                } else {
                    this.isModalShown = false;
                }
            },
            'workOrderToSave.number': {
                handler(value) {
                    (async () => {
                        this.checkWorkOrderNumber(value);
                    })();
                },
            },
            'workOrderToSave.startDate': {
                immediate: true,
                handler(startDate) {
                    if (!!startDate && !!this.workOrderToSave.completedDate) {
                        this.invalidDateRange = new Date(startDate) > new Date(this.workOrderToSave.completedDate);
                    } else {
                        this.invalidDateRange = false;
                    }
                },
            },
            'workOrderToSave.completedDate': {
                immediate: true,
                handler(completedDate) {
                    if (!!this.workOrderToSave.startDate && !!completedDate) {
                        this.invalidDateRange = new Date(completedDate) < new Date(this.workOrderToSave.startDate);
                    } else {
                        this.invalidDateRange = false;
                    }
                },
            },
        },
    };
</script>

<style lang="scss" scoped>
    .work-orders {
        &__account {
            width: inherit;

            &-add {
                height: 2.4rem;
                margin-right: 0px !important;
            }
        }

        &__feedback {
            line-height: 0.6rem;
            margin-bottom: 0.5rem;
        }

        &__reporter {
            &-group {
                width: inherit;
            }

            &-select {
                margin-right: 0.2rem;
                padding: 0px !important;
            }

            &-option {
                margin-left: 0.2rem;
                padding: 0px !important;
            }
        }
    }

    .label {
        display: inline-block;
        margin-bottom: 0.5rem;
    }

    :deep(.modal-body) {
        max-height: 80vh;
        overflow-y: auto;
    }

    @media (max-width: 770px) {
        .work-orders__reporter {
            &-group {
                flex-direction: column;
            }

            &-select,
            &-option {
                margin: 0.5rem 0;
            }
        }
    }
</style>
